/**
 * 🔒 AdminAuditLog — page admin lecture seule
 *
 * GET /api/admin/audit-log avec filtres (admin, action, entity_type, période)
 * + bouton Export CSV (GET /api/admin/audit-log/export)
 *
 * Mobile-first, premium UI alignée avec le reste du dashboard admin.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ShieldCheck, Filter, Download, Clock, User, Activity, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';

const PAGE_SIZE = 50;

const ACTION_LABELS = {
  remove_coach: { label: 'Coach retiré', color: '#92710A', bg: '#FBF9F3' },
  delete_quote: { label: 'Devis supprimé', color: '#0EA5E9', bg: '#F0F9FF' },
  delete_order: { label: 'Commande supprimée', color: '#991B1B', bg: '#FEF2F2' },
};

const ENTITY_LABELS = {
  coach: 'Coach',
  quote_request: 'Demande devis',
  enterprise_order: 'Commande',
};

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ actions: [], entity_types: [], admins: [] });
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);

  const [filterAdmin, setFilterAdmin] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (filterAdmin) params.set('admin_id', filterAdmin);
    if (filterAction) params.set('action', filterAction);
    if (filterEntityType) params.set('entity_type', filterEntityType);
    if (filterDateFrom) params.set('date_from', filterDateFrom);
    if (filterDateTo) params.set('date_to', filterDateTo);
    params.set('limit', PAGE_SIZE);
    params.set('offset', offset);
    return params.toString();
  }, [filterAdmin, filterAction, filterEntityType, filterDateFrom, filterDateTo, offset]);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/admin/audit-log?${buildQuery()}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setFilters(data.filters || { actions: [], entity_types: [], admins: [] });
    } catch (err) {
      toast.error(`Erreur chargement audit : ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const handleExportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filterAdmin) params.set('admin_id', filterAdmin);
      if (filterAction) params.set('action', filterAction);
      if (filterEntityType) params.set('entity_type', filterEntityType);
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo) params.set('date_to', filterDateTo);
      const res = await fetch(`${getApiUrl()}/admin/audit-log/export?${params.toString()}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `admin-audit-log-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch (err) {
      toast.error(`Échec export : ${err.message}`);
    }
  };

  const resetFilters = () => {
    setFilterAdmin('');
    setFilterAction('');
    setFilterEntityType('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setOffset(0);
  };

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'medium' });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F3]" data-testid="admin-audit-log-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="p-2 rounded-xl bg-white border border-[#E8E5DE] hover:border-[#92710A] transition" data-testid="audit-log-back">
              <ArrowLeft className="w-4 h-4 text-[#4B5563]" />
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#92710A]" />
                <h1 className="text-xl sm:text-2xl font-display text-[#111827]">Audit Log Admin</h1>
              </div>
              <p className="text-xs text-[#6B7280] mt-1">Traçabilité des actions sensibles (Qualiopi / RGPD)</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={fetchLog}
              data-testid="audit-log-refresh"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-[#E8E5DE] text-[#4B5563] text-sm hover:border-[#92710A] transition">
              <RefreshCw className="w-4 h-4" /> Actualiser
            </button>
            <button onClick={handleExportCsv}
              data-testid="audit-log-export-csv"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#111827] text-white text-sm hover:bg-[#92710A] transition">
              <Download className="w-4 h-4" /> Exporter CSV
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white border border-[#E8E5DE] rounded-2xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-[#92710A]" />
            <span className="text-xs uppercase tracking-wider font-medium text-[#4B5563]">Filtres</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <select value={filterAdmin} onChange={(e) => { setOffset(0); setFilterAdmin(e.target.value); }}
              data-testid="audit-filter-admin"
              className="border border-[#E8E5DE] rounded-lg px-3 py-2 text-sm bg-white text-[#111827]">
              <option value="">Tous admins</option>
              {filters.admins.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={filterAction} onChange={(e) => { setOffset(0); setFilterAction(e.target.value); }}
              data-testid="audit-filter-action"
              className="border border-[#E8E5DE] rounded-lg px-3 py-2 text-sm bg-white text-[#111827]">
              <option value="">Toutes actions</option>
              {filters.actions.map(a => (
                <option key={a} value={a}>{ACTION_LABELS[a]?.label || a}</option>
              ))}
            </select>
            <select value={filterEntityType} onChange={(e) => { setOffset(0); setFilterEntityType(e.target.value); }}
              data-testid="audit-filter-entity"
              className="border border-[#E8E5DE] rounded-lg px-3 py-2 text-sm bg-white text-[#111827]">
              <option value="">Tous types</option>
              {filters.entity_types.map(e => (
                <option key={e} value={e}>{ENTITY_LABELS[e] || e}</option>
              ))}
            </select>
            <input type="date" value={filterDateFrom} onChange={(e) => { setOffset(0); setFilterDateFrom(e.target.value); }}
              data-testid="audit-filter-date-from"
              placeholder="Du"
              className="border border-[#E8E5DE] rounded-lg px-3 py-2 text-sm bg-white text-[#111827]" />
            <input type="date" value={filterDateTo} onChange={(e) => { setOffset(0); setFilterDateTo(e.target.value); }}
              data-testid="audit-filter-date-to"
              placeholder="Au"
              className="border border-[#E8E5DE] rounded-lg px-3 py-2 text-sm bg-white text-[#111827]" />
          </div>
          {(filterAdmin || filterAction || filterEntityType || filterDateFrom || filterDateTo) && (
            <button onClick={resetFilters}
              data-testid="audit-filter-reset"
              className="mt-3 text-xs text-[#92710A] hover:underline">
              Réinitialiser les filtres
            </button>
          )}
        </div>

        {/* Compteur */}
        <div className="text-xs text-[#6B7280] mb-3">
          {loading ? 'Chargement…' : `${total} entrée${total > 1 ? 's' : ''} • affichage ${offset + 1}-${Math.min(offset + items.length, total)}`}
        </div>

        {/* Liste */}
        {loading ? (
          <div className="text-center py-16 text-[#6B7280]">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-[#6B7280] bg-white border border-[#E8E5DE] rounded-2xl">
            Aucune entrée pour ces filtres.
          </div>
        ) : (
          <div className="space-y-2" data-testid="audit-log-list">
            {items.map((it, i) => {
              const actionMeta = ACTION_LABELS[it.action] || { label: it.action, color: '#4B5563', bg: '#F3F4F6' };
              return (
                <motion.div key={`${it.timestamp}-${it.entity_id}-${i}`}
                  data-testid={`audit-log-row-${it.entity_id}`}
                  initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.015 }}
                  className="bg-white border border-[#E8E5DE] rounded-2xl p-4">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: actionMeta.bg, color: actionMeta.color }}>
                      <Activity className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full"
                          style={{ color: actionMeta.color, backgroundColor: actionMeta.bg }}>
                          {actionMeta.label}
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-[#4B5563] bg-[#F8F7F4] px-2 py-0.5 rounded-full">
                          {ENTITY_LABELS[it.entity_type] || it.entity_type}
                        </span>
                      </div>
                      <div className="text-sm text-[#111827] mt-1.5 font-mono break-all">
                        {it.entity_id}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-[#6B7280] mt-1.5 flex-wrap">
                        <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {it.admin_id}</span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(it.timestamp)}</span>
                      </div>
                      {it.metadata && Object.keys(it.metadata).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-[11px] text-[#92710A] cursor-pointer hover:underline">Voir le contexte</summary>
                          <pre className="mt-2 text-[11px] text-[#4B5563] bg-[#FBF9F3] border border-[#E8DFC8] rounded-lg p-2 overflow-x-auto">
                            {JSON.stringify(it.metadata, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              data-testid="audit-log-prev"
              className="px-3 py-2 rounded-lg bg-white border border-[#E8E5DE] text-sm text-[#4B5563] disabled:opacity-40 hover:border-[#92710A] transition">
              ← Précédent
            </button>
            <span className="text-xs text-[#6B7280] px-2">
              {Math.floor(offset / PAGE_SIZE) + 1} / {Math.ceil(total / PAGE_SIZE)}
            </span>
            <button disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              data-testid="audit-log-next"
              className="px-3 py-2 rounded-lg bg-white border border-[#E8E5DE] text-sm text-[#4B5563] disabled:opacity-40 hover:border-[#92710A] transition">
              Suivant →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
